import { Stack, Text } from '@hotjar/hotsauce';
import { ComponentType } from 'react';
import { ErrorBoundary as ErrBoundary, withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

const isDailyError = (error: Error) => {
  return (
    error?.name?.includes('Daily') ||
    error?.message?.includes('daily.co') ||
    error?.stack?.includes('daily.co')
  );
};

export const ErrorFallbackComponent = () => {
  const { t } = useTranslation('engage', { keyPrefix: 'overview' });

  return (
    <Stack>
      <Text as="h5" textType="header5">
        {t('error.generic')}
      </Text>
    </Stack>
  );
};

export const ErrorBoundary = ({
  children,
  componentName,
}: {
  children: JSX.Element;
  componentName: string;
}) => {
  const handleOnError = (error: Error) => {
    // We want to separate errors for daily and our application;
    // This is recommended way of doing this;
    const errorType = isDailyError(error) ? 'daily_error' : 'lobby_error';

    console.error(error.message, {
      component: componentName,
      error_type: errorType,
      error_source: isDailyError(error) ? 'daily.co' : 'application',
    });
  };

  return (
    <ErrBoundary FallbackComponent={ErrorFallbackComponent} onError={handleOnError}>
      {children}
    </ErrBoundary>
  );
};

export function WithErrorBoundary<TComponentProps extends object>(
  Component: ComponentType<React.PropsWithChildren<TComponentProps>>,
  name: string,
) {
  function Wrapped(props: TComponentProps) {
    return <Component {...props} />;
  }

  return withErrorBoundary(Wrapped, {
    fallback: <ErrorFallbackComponent />,
    onError(error) {
      console.error(error.message, { component: name });
    },
  });
}
